/**
 * React Query key factory — INSTRUCTORS keys (EPIC 05 INSTR).
 *
 * Référence centrale dans `queryKeys.ts` (barrel). Graphe d'invalidation gravé
 * (_INTRO §6bis.2) : ① valider/révoquer une exigence → requirements(id) +
 * requirementsMatrix + coverage + stats(id) (+ invalidation des listings
 * DataTable) ; ② toute mutation tarifaire → rates(id) (resolve inclus par
 * préfixe) + mapCard(id) + invalidation des listings (colonne planning_ready).
 *
 * ⚠️ Le listing serveur (DataTable mode="server") a ses PROPRES clés = la chaîne
 * d'endpoint brute (`['/api/instructors']`, `['datatable-groups', …]`,
 * `['datatable-facets', …]`) — que `instructors.all` ne préfixe PAS. Ne les
 * recopiez pas à la main : `invalidateDataTableListings(qc, '/api/instructors',
 * queryKeys.instructors.all)` (`@/lib/datatable-invalidate`) couvre les quatre,
 * et c'est la quatrième — les compteurs du panneau de filtres — que les blocs
 * recopiés oublient systématiquement.
 *
 * ⚠️ N'ajoutez ici que des clés RÉELLEMENT lues par une `useQuery`. Une entrée
 * qui n'est plus qu'une cible d'invalidation fait croire à un contrat qui
 * n'existe pas, et sa disparition ne casse rien : elle survit indéfiniment.
 */

const instructorsAll = ['instructors'] as const

export const instructorsKeys = {
    instructors: {
        all: instructorsAll,
        // ⚠️ Pas de `list()` ici : le vivier est un `<DataTable mode="server">`,
        // dont les clés sont la chaîne d'endpoint brute (`['/api/instructors']`).
        // Une entrée `list()` de plus dans cette fabrique ferait croire à un
        // contrat qui n'existe pas — invalider une clé que personne ne lit.
        detail: (id: string, entityId: string) =>
            [...instructorsAll, 'detail', id, entityId] as const,
        detailAny: (id: string) => [...instructorsAll, 'detail', id] as const,
        // ⚠️ `entityId` FAIT PARTIE DE LA CLÉ. L'entité voyage en en-tête HTTP, donc
        // invisible de React Query : sans elle ici, changer d'entité reservait les
        // résultats de la précédente depuis le cache — un intervenant d'une autre
        // filiale proposé à la sélection, sans le moindre appel réseau.
        lookup: (entityId: string | null | undefined, search?: string) =>
            [...instructorsAll, 'lookup', entityId, search] as const,
        prefillFromMe: () => [...instructorsAll, 'prefillFromMe'] as const,
        // Carte (INSTR-003) — bbox + filtres (nature/departments/statut) + popover.
        map: (params?: Record<string, unknown>) => [...instructorsAll, 'map', params] as const,
        mapCard: (id: string, addressId?: string) =>
            [...instructorsAll, 'mapCard', id, addressId] as const,
        // Satellites de la fiche (onglets).
        addresses: (id: string) => [...instructorsAll, 'addresses', id] as const,
        capabilities: (id: string) => [...instructorsAll, 'capabilities', id] as const,
        requirements: (id: string) => [...instructorsAll, 'requirements', id] as const,
        // Sous-clé de `requirements(id)` → l'invalidation par préfixe après une
        // revalidation rafraîchit l'historique en même temps que la vue, ce qui
        // est exactement ce qu'on veut : la validation qui vient d'être
        // remplacée doit APPARAÎTRE dans l'historique sans rechargement.
        requirementHistory: (id: string) =>
            [...instructorsAll, 'requirements', id, 'history'] as const,
        rates: (id: string) => [...instructorsAll, 'rates', id] as const,
        // ratesResolve = sous-clé de rates(id) → invalidation par préfixe.
        ratesResolve: (id: string, productId?: string, date?: string) =>
            [...instructorsAll, 'rates', id, 'resolve', productId, date] as const,
        // INSTR-033 — factures (le détail vit sous la même clé : invalider la
        // liste invalide le détail par préfixe).
        invoices: (id: string) => [...instructorsAll, 'invoices', id] as const,
        travel: (id: string) => [...instructorsAll, 'travel', id] as const,
        constraints: (id: string) => [...instructorsAll, 'constraints', id] as const,
        limits: (id: string) => [...instructorsAll, 'limits', id] as const,
        compliance: (id: string) => [...instructorsAll, 'compliance', id] as const,
        members: (id: string) => [...instructorsAll, 'members', id] as const,
        // PRÉFIXE de `members(id)` : toutes les listes de membres, de tous les
        // organismes. Chaque ligne porte la colonne « Exigences » de la PERSONNE
        // (`requirements.expired_count`, servi par `list_members`) : valider une
        // exigence sur SA fiche change sa ligne dans chaque organisme où elle est
        // membre, et la fiche ne sait pas lesquels.
        membersAny: () => [...instructorsAll, 'members'] as const,
        affiliations: (id: string) => [...instructorsAll, 'affiliations', id] as const,
        // GED fiche intervenant (INSTR-009) : le cache vit sous `entityDocumentsKeys`
        // (`@/lib/queryKeys.documents`, kind:'instructor'), pas ici — voir ce module.
        // INSTR-010 — CV généré. `cv(id)` est le PRÉFIXE d'invalidation (il
        // couvre la fiche vue depuis n'importe quelle entité : une fiche héritée
        // est lisible depuis plusieurs) ; `cvState` est la clé réellement lue.
        // ⚠️ `cvVersions` n'est PAS une sous-clé de `cv` — l'historique a son
        // propre segment, il faut donc l'invalider explicitement.
        cv: (id: string) => [...instructorsAll, 'cv', id] as const,
        cvState: (id: string, entityId: string) =>
            [...instructorsAll, 'cv', id, entityId] as const,
        cvVersions: (id: string, entityId: string) =>
            [...instructorsAll, 'cv-versions', id, entityId] as const,
        cvVersionsAny: (id: string) => [...instructorsAll, 'cv-versions', id] as const,
        stats: (id: string) => [...instructorsAll, 'stats', id] as const,
        // INSTR-011 §8.2 — compteurs « privilégié / évité » (alimentés par CLI-013).
        clientSignals: (id: string) => [...instructorsAll, 'clientSignals', id] as const,
        activities: (id: string) => [...instructorsAll, 'activities', id] as const,
        // Création assistée IA (INSTR-015) — polling d'une extraction.
        aiExtraction: (id: string) => [...instructorsAll, 'aiExtraction', id] as const,
        // Demandes envoyées (INSTR-016) — file de validation + console.
        actionLinks: () => [...instructorsAll, 'actionLinks'] as const,
        // Compteurs de la console : pilotent la visibilité de l'entrée de nav
        // et sa pastille. Clé dédiée (rafraîchie toutes les 60 s côté sidebar).
        actionLinksSummary: (entityId: string) =>
            [...instructorsAll, 'actionLinks', 'summary', entityId] as const,
        // PASS-007 — l'état du questionnaire d'UNE exigence, sur la fiche.
        // Clé par (intervenant, exigence) : la modale d'une exigence ne doit pas
        // afficher le suivi d'une autre en attendant son propre chargement.
        requirementQuiz: (instructorId: string, requirementId: string) =>
            [...instructorsAll, 'requirementQuiz', instructorId, requirementId] as const,
        // Cockpits BUSINESS (INSTR-017/018).
        requirementsMatrix: () => [...instructorsAll, 'requirementsMatrix'] as const,
        coverage: () => [...instructorsAll, 'coverage'] as const,
    },
} as const
